// Playable classes and their skill trees.
// Every skill is data: the simulation interprets `effects`, the client reads the same
// definition to show tooltips, cooldowns, and mana costs.

import { ClassDef, ClassId, EffectDef, SkillDef, rv } from './types';

const pct = (n: number) => `${Math.round(n * 100)}%`;

function dmgLine(rank: number, mult: [number, number] | number, word = 'damage'): string {
  return `${pct(rv(mult, rank))} ${word}`;
}

type S = Omit<SkillDef, 'maxRank'> & { maxRank?: number };

function def(s: S): SkillDef {
  return { maxRank: 10, ...s };
}

const list: SkillDef[] = [
  // ───────────────────────── REAVER (melee) ─────────────────────────
  def({
    id: 'cleave', cls: 'reaver', name: 'Cleave', tier: 1, icon: 'cleave',
    desc: 'A wide swing that strikes every foe in front of you.',
    mana: 0, cd: 0, cast: 0.14, recover: 0.26, scale: 'melee', target: 'point', reach: 2.0,
    anim: 'swing', sfx: 'swing',
    effects: [{ k: 'arc', range: 2.1, angle: 130, mult: [1.0, 0.14], el: 'phys', vis: 'slash' }],
    info: (r) => [dmgLine(r, [1.0, 0.14]), 'Wide arc'],
  }),
  def({
    id: 'rend', cls: 'reaver', name: 'Rend', tier: 1, icon: 'rend',
    desc: 'A vicious cut that leaves the target bleeding.',
    mana: [3, 0.4], cd: 0.6, cast: 0.14, recover: 0.3, scale: 'melee', target: 'point', reach: 2.0,
    anim: 'swing', sfx: 'swing',
    effects: [{ k: 'arc', range: 2.1, angle: 70, mult: [1.5, 0.2], el: 'phys', vis: 'slashRed', status: { id: 'bleed', dur: 3, dot: [0.25, 0.04] } }],
    info: (r) => [dmgLine(r, [1.5, 0.2]), `Bleeds for ${pct(rv([0.25, 0.04], r) * 3)} over 3s`],
  }),
  def({
    id: 'leapslam', cls: 'reaver', name: 'Leap Slam', tier: 3, icon: 'leap',
    desc: 'Vault to the target location and crush everything where you land.',
    mana: [8, 0.8], cd: 5, cast: 0.1, recover: 0.2, scale: 'melee', target: 'point',
    anim: 'slam', sfx: 'slam',
    effects: [{ k: 'leap', maxDist: [8, 0.3], r: 3, mult: [1.6, 0.22], el: 'phys', status: { id: 'stun', dur: 0.9 }, vis: 'shockwave' }],
    info: (r) => [dmgLine(r, [1.6, 0.22], 'landing damage'), `Range ${rv([8, 0.3], r).toFixed(1)}`, 'Stuns 0.9s'],
  }),
  def({
    id: 'warcry', cls: 'reaver', name: 'War Cry', tier: 6, icon: 'warcry',
    desc: 'A terrible shout empowers nearby allies and terrifies foes.',
    mana: [10, 1], cd: 16, cast: 0.15, recover: 0.2, scale: 'none', target: 'self',
    anim: 'cast', sfx: 'shout',
    effects: [
      { k: 'buff', buff: 'warcry', r: 10, dur: [10, 1], mods: { dmgPct: [20, 5], armorPct: [10, 2] }, bit: 1024, vis: 'buffRed' },
      { k: 'debuff', r: [6, 0.2], at: 'self', status: { id: 'fear', dur: 2 }, vis: 'shockwaveRed' },
    ],
    info: (r) => [`+${rv([20, 5], r)}% party damage`, `+${rv([10, 2], r)}% armor`, `${rv([10, 1], r)}s, fears nearby foes`],
  }),
  def({
    id: 'whirlwind', cls: 'reaver', name: 'Whirlwind', tier: 10, icon: 'whirl',
    desc: 'Become a storm of steel, carving through everything as you run.',
    mana: [14, 1.2], cd: 6, cast: 0.05, recover: 0.1, scale: 'melee', target: 'point',
    anim: 'swing', sfx: 'whirl',
    effects: [{ k: 'whirl', dur: [2.5, 0.15], r: 2.4, every: 0.25, mult: [0.8, 0.09], el: 'phys', speed: 1.25, vis: 'whirl' }],
    info: (r) => [dmgLine(r, [0.8, 0.09], 'per hit'), `${rv([2.5, 0.15], r).toFixed(1)}s duration`],
  }),
  def({
    id: 'bloodlust', cls: 'reaver', name: 'Bloodlust', tier: 14, icon: 'bloodlust',
    desc: 'Enter a frenzy. Your strikes come faster and drink the blood of the fallen.',
    mana: [12, 1], cd: 20, cast: 0.1, recover: 0.1, scale: 'none', target: 'self',
    anim: 'cast', sfx: 'buff',
    effects: [{ k: 'buff', buff: 'bloodlust', r: 0, dur: [8, 0.6], mods: { atkSpd: [30, 4], lifeSteal: [6, 1], moveSpd: [12, 1] }, bit: 1024, vis: 'buffRed' }],
    info: (r) => [`+${rv([30, 4], r)}% attack speed`, `+${rv([6, 1], r)}% life steal`, `${rv([8, 0.6], r).toFixed(1)}s`],
  }),
  def({
    id: 'earthsplitter', cls: 'reaver', name: 'Earthsplitter', tier: 18, icon: 'earth',
    desc: 'Hurl a shockwave along the ground that carves through every foe.',
    mana: [16, 1.4], cd: 3, cast: 0.2, recover: 0.25, scale: 'melee', target: 'point',
    anim: 'slam', sfx: 'slam',
    effects: [{ k: 'proj', proj: { vis: 'wave', speed: 11, life: 0.9, r: 0.9, mult: [2.2, 0.28], el: 'phys', pierce: 99, knock: 2 } }],
    info: (r) => [dmgLine(r, [2.2, 0.28]), 'Pierces all foes'],
  }),
  def({
    id: 'guillotine', cls: 'reaver', name: 'Guillotine', tier: 24, icon: 'guillotine',
    desc: 'A devastating downward chop. Deals massive bonus damage to wounded foes.',
    mana: [20, 1.6], cd: 8, cast: 0.3, recover: 0.3, scale: 'melee', target: 'point', reach: 2.2,
    anim: 'slam', sfx: 'slam',
    effects: [{ k: 'arc', range: 2.4, angle: 90, mult: [4.0, 0.5], el: 'phys', vis: 'slashBig', execute: 2.0 }],
    info: (r) => [dmgLine(r, [4.0, 0.5]), 'Triple damage below 30% HP'],
  }),

  // ───────────────────────── SPELLWEAVER (magic) ─────────────────────────
  def({
    id: 'firebolt', cls: 'spellweaver', name: 'Fire Bolt', tier: 1, icon: 'firebolt',
    desc: 'A quick bolt of flame that ignites its target.',
    mana: [1, 0.15], cd: 0, cast: 0.1, recover: 0.28, scale: 'spell', target: 'point',
    anim: 'cast', sfx: 'fire',
    effects: [{ k: 'proj', proj: { vis: 'firebolt', speed: 13, life: 1.1, r: 0.3, mult: [1.0, 0.14], el: 'fire', status: { id: 'burn', dur: 2, dot: 0.2, chance: 0.5 } } }],
    info: (r) => [dmgLine(r, [1.0, 0.14], 'fire damage'), 'May ignite'],
  }),
  def({
    id: 'iceshard', cls: 'spellweaver', name: 'Ice Shard', tier: 1, icon: 'iceshard',
    desc: 'A piercing lance of ice that chills everything in its path.',
    mana: [3, 0.3], cd: 0.5, cast: 0.14, recover: 0.3, scale: 'spell', target: 'point',
    anim: 'cast', sfx: 'ice',
    effects: [{ k: 'proj', proj: { vis: 'icebolt', speed: 11, life: 1.1, r: 0.3, mult: [1.1, 0.15], el: 'cold', pierce: [2, 0.34], status: { id: 'chill', dur: 2.5 } } }],
    info: (r) => [dmgLine(r, [1.1, 0.15], 'cold damage'), `Pierces ${Math.floor(rv([2, 0.34], r))}`, 'Chills'],
  }),
  def({
    id: 'blink', cls: 'spellweaver', name: 'Blink', tier: 3, icon: 'blink',
    desc: 'Fold space and reappear at the target location.',
    mana: [8, 0], cd: [6, -0.3], cast: 0.05, recover: 0.05, scale: 'none', target: 'point',
    anim: 'cast', sfx: 'warp',
    effects: [{ k: 'blink', maxDist: [8, 0.5], vis: 'warpPuff' }],
    info: (r) => [`Range ${rv([8, 0.5], r).toFixed(1)}`, `${rv([6, -0.3], r).toFixed(1)}s cooldown`],
  }),
  def({
    id: 'frostnova', cls: 'spellweaver', name: 'Frost Nova', tier: 6, icon: 'nova',
    desc: 'A freezing blast erupts from you, locking enemies in ice.',
    mana: [12, 1], cd: 6, cast: 0.15, recover: 0.2, scale: 'spell', target: 'self',
    anim: 'cast', sfx: 'ice',
    effects: [{ k: 'nova', r: [5, 0.15], mult: [1.0, 0.15], el: 'cold', at: 'self', status: { id: 'freeze', dur: [1.4, 0.08] }, vis: 'frostnova' }],
    info: (r) => [dmgLine(r, [1.0, 0.15], 'cold damage'), `Freezes ${rv([1.4, 0.08], r).toFixed(1)}s`],
  }),
  def({
    id: 'chainlight', cls: 'spellweaver', name: 'Chain Lightning', tier: 10, icon: 'chain',
    desc: 'Lightning leaps from foe to foe, shocking them.',
    mana: [12, 1], cd: 1.2, cast: 0.14, recover: 0.25, scale: 'spell', target: 'point',
    anim: 'cast', sfx: 'zap',
    effects: [{ k: 'chain', range: 9, jumps: [4, 0.5], mult: [1.8, 0.24], el: 'light', falloff: 0.85, status: { id: 'shock', dur: 2 }, vis: 'lightning' }],
    info: (r) => [dmgLine(r, [1.8, 0.24], 'lightning'), `${Math.floor(rv([4, 0.5], r))} jumps`],
  }),
  def({
    id: 'manashield', cls: 'spellweaver', name: 'Arcane Ward', tier: 14, icon: 'shield',
    desc: 'A shimmering ward hardens against all harm.',
    mana: [15, 1], cd: 18, cast: 0.1, recover: 0.1, scale: 'none', target: 'self',
    anim: 'cast', sfx: 'buff',
    effects: [{ k: 'buff', buff: 'manashield', r: 0, dur: [8, 0.5], mods: { dmgReduce: [25, 2.5], mpRegen: [4, 0.6] }, bit: 512, vis: 'buffBlue' }],
    info: (r) => [`${rv([25, 2.5], r)}% damage reduction`, `${rv([8, 0.5], r).toFixed(1)}s`],
  }),
  def({
    id: 'meteor', cls: 'spellweaver', name: 'Meteor', tier: 18, icon: 'meteor',
    desc: 'Call down a burning star. The impact leaves the ground aflame.',
    mana: [24, 2], cd: 5, cast: 0.25, recover: 0.25, scale: 'spell', target: 'point',
    anim: 'cast', sfx: 'fire',
    effects: [
      { k: 'nova', r: [3.6, 0.1], mult: [4.2, 0.55], el: 'fire', at: 'target', delay: 0.9, vis: 'meteor', status: { id: 'burn', dur: 3, dot: 0.1 } },
      { k: 'ground', r: 2.8, dur: 4, every: 0.5, mult: [0.45, 0.06], el: 'fire', at: 'target', vis: 'fireGround' },
    ],
    info: (r) => [dmgLine(r, [4.2, 0.55], 'impact'), `${pct(rv([0.45, 0.06], r))} burn per tick`],
  }),
  def({
    id: 'singularity', cls: 'spellweaver', name: 'Singularity', tier: 24, icon: 'void',
    desc: 'Tear open a tiny black hole which drags in and grinds apart all foes.',
    mana: [30, 2.5], cd: 14, cast: 0.3, recover: 0.3, scale: 'spell', target: 'point',
    anim: 'cast', sfx: 'warp',
    effects: [{ k: 'ground', r: [5.5, 0.15], dur: [4, 0.2], every: 0.35, mult: [0.8, 0.1], el: 'magic', at: 'target', pull: 3.5, vis: 'singularity' }],
    info: (r) => [dmgLine(r, [0.8, 0.1], 'magic per tick'), 'Pulls enemies'],
  }),

  // ───────────────────────── GUNNER (guns & explosives) ─────────────────────────
  def({
    id: 'blaster', cls: 'gunner', name: 'Blaster Shot', tier: 1, icon: 'blaster',
    desc: 'A fast, precise shot from your sidearm.',
    mana: 0, cd: 0, cast: 0.05, recover: 0.24, scale: 'ranged', target: 'point',
    anim: 'shoot', sfx: 'shoot',
    effects: [{ k: 'proj', proj: { vis: 'bullet', speed: 22, life: 0.7, r: 0.2, mult: [0.95, 0.13], el: 'phys' } }],
    info: (r) => [dmgLine(r, [0.95, 0.13])],
  }),
  def({
    id: 'scatter', cls: 'gunner', name: 'Scattergun', tier: 1, icon: 'scatter',
    desc: 'A devastating spread of pellets at short range.',
    mana: [3, 0.3], cd: 0.9, cast: 0.08, recover: 0.3, scale: 'ranged', target: 'point',
    anim: 'shoot', sfx: 'shotgun',
    effects: [{ k: 'proj', proj: { vis: 'pellet', speed: 18, life: 0.34, r: 0.22, mult: [0.5, 0.07], el: 'phys', count: [5, 0.34], spread: 50, knock: 0.8 } }],
    info: (r) => [dmgLine(r, [0.5, 0.07], 'per pellet'), `${Math.floor(rv([5, 0.34], r))} pellets`],
  }),
  def({
    id: 'grenade', cls: 'gunner', name: 'Frag Grenade', tier: 3, icon: 'grenade',
    desc: 'Lob a grenade which detonates at the target location.',
    mana: [6, 0.6], cd: 2.2, cast: 0.1, recover: 0.25, scale: 'ranged', target: 'point',
    anim: 'shoot', sfx: 'throw',
    effects: [{ k: 'proj', proj: { vis: 'grenade', speed: 13, life: 0.9, r: 0.2, mult: 0, el: 'phys', lob: true, explode: { r: 3, mult: [1.8, 0.24], el: 'fire', vis: 'explosion', knock: 2 } } }],
    info: (r) => [dmgLine(r, [1.8, 0.24], 'blast damage'), 'Radius 3'],
  }),
  def({
    id: 'mine', cls: 'gunner', name: 'Proximity Mine', tier: 6, icon: 'mine',
    desc: 'Deploy a mine which detonates when foes wander close.',
    mana: [8, 0.7], cd: 1.2, cast: 0.15, recover: 0.2, scale: 'ranged', target: 'point',
    anim: 'shoot', sfx: 'throw',
    effects: [{ k: 'mine', r: 3.5, trigger: 1.6, life: 40, mult: [2.6, 0.34], el: 'fire', max: [3, 0.34], vis: 'mine', status: { id: 'stun', dur: 0.6 } }],
    info: (r) => [dmgLine(r, [2.6, 0.34], 'blast damage'), `Up to ${Math.floor(rv([3, 0.34], r))} mines`],
  }),
  def({
    id: 'turret', cls: 'gunner', name: 'Sentry Turret', tier: 10, icon: 'turret',
    desc: 'Deploy an automated turret that fires on anything hostile.',
    mana: [18, 1.4], cd: 6, cast: 0.2, recover: 0.25, scale: 'ranged', target: 'point',
    anim: 'cast', sfx: 'deploy',
    effects: [{ k: 'summon', unit: 's_turret', count: 1, max: [1, 0.25], dur: [18, 2], vis: 'warpPuff' }],
    info: (r) => [`${rv([18, 2], r)}s lifetime`, `Up to ${Math.floor(rv([1, 0.25], r))} turrets`, `Damage scales with rank`],
  }),
  def({
    id: 'stim', cls: 'gunner', name: 'Combat Stim', tier: 14, icon: 'stim',
    desc: 'Inject a cocktail of stimulants. Heals, and speeds up everything you do.',
    mana: [10, 0.8], cd: 18, cast: 0.05, recover: 0.05, scale: 'none', target: 'self',
    anim: 'cast', sfx: 'buff',
    effects: [
      { k: 'heal', pct: [20, 2], r: 0, vis: 'healPuff' },
      { k: 'buff', buff: 'stim', r: 0, dur: [8, 0.5], mods: { atkSpd: [25, 3], moveSpd: [20, 2] }, bit: 1024, vis: 'buffGreen' },
    ],
    info: (r) => [`Heals ${rv([20, 2], r)}%`, `+${rv([25, 3], r)}% attack speed`, `+${rv([20, 2], r)}% move speed`],
  }),
  def({
    id: 'rockets', cls: 'gunner', name: 'Rocket Barrage', tier: 18, icon: 'rocket',
    desc: 'Fire a salvo of homing rockets.',
    mana: [18, 1.5], cd: 3, cast: 0.15, recover: 0.3, scale: 'ranged', target: 'point',
    anim: 'shoot', sfx: 'rocket',
    effects: [{ k: 'proj', proj: { vis: 'rocket', speed: 10, life: 1.6, r: 0.3, mult: [0.5, 0.05], el: 'phys', count: [3, 0.34], spread: 36, homing: 3.5, explode: { r: 2.2, mult: [1.2, 0.15], el: 'fire', vis: 'explosion' } } }],
    info: (r) => [`${Math.floor(rv([3, 0.34], r))} rockets`, dmgLine(r, [1.2, 0.15], 'blast each')],
  }),
  def({
    id: 'orbital', cls: 'gunner', name: 'Orbital Strike', tier: 24, icon: 'orbital',
    desc: 'Designate a target. Moments later, the sky answers.',
    mana: [30, 2.5], cd: 12, cast: 0.3, recover: 0.3, scale: 'ranged', target: 'point',
    anim: 'cast', sfx: 'zap',
    effects: [{ k: 'nova', r: [5.5, 0.15], mult: [7, 0.9], el: 'magic', at: 'target', delay: 1.3, vis: 'orbital', knock: 3 }],
    info: (r) => [dmgLine(r, [7, 0.9]), '1.3s delay, huge radius'],
  }),

  // ───────────────────────── CLERIC (healer) ─────────────────────────
  def({
    id: 'holybolt', cls: 'cleric', name: 'Holy Bolt', tier: 1, icon: 'holybolt',
    desc: 'Radiant bolt that burns enemies and passes through allies, healing them.',
    mana: [1, 0.15], cd: 0, cast: 0.1, recover: 0.3, scale: 'spell', target: 'point',
    anim: 'cast', sfx: 'holy',
    effects: [{ k: 'proj', proj: { vis: 'holy', speed: 12, life: 1, r: 0.32, mult: [0.95, 0.13], el: 'magic', heals: [4, 0.5] } }],
    info: (r) => [dmgLine(r, [0.95, 0.13], 'magic damage'), `Heals allies ${rv([4, 0.5], r)}%`],
  }),
  def({
    id: 'mend', cls: 'cleric', name: 'Mend', tier: 1, icon: 'heal',
    desc: 'Mend the wounds of the ally nearest your cursor, or yourself.',
    mana: [5, 0.5], cd: 0.8, cast: 0.15, recover: 0.2, scale: 'none', target: 'ally',
    anim: 'cast', sfx: 'heal',
    effects: [{ k: 'heal', pct: [14, 2], r: -1, vis: 'healPuff', flat: [10, 8] }],
    info: (r) => [`Heals ${rv([14, 2], r)}% + ${rv([10, 8], r)}`],
  }),
  def({
    id: 'blessing', cls: 'cleric', name: 'Blessing', tier: 3, icon: 'bless',
    desc: 'Bless all nearby allies, hardening their skin and speeding recovery.',
    mana: [10, 1], cd: 14, cast: 0.2, recover: 0.2, scale: 'none', target: 'self',
    anim: 'cast', sfx: 'buff',
    effects: [{ k: 'buff', buff: 'blessing', r: 12, dur: [14, 1.5], mods: { armorPct: [20, 4], hpRegen: [2, 1.2], resAll: [5, 2] }, bit: 4096, vis: 'buffGold' }],
    info: (r) => [`+${rv([20, 4], r)}% armor`, `+${rv([2, 1.2], r).toFixed(1)} HP/sec`, `+${rv([5, 2], r)} all res`],
  }),
  def({
    id: 'holynova', cls: 'cleric', name: 'Holy Nova', tier: 6, icon: 'holynova',
    desc: 'A burst of light scours enemies and heals allies around you.',
    mana: [12, 1], cd: 4, cast: 0.15, recover: 0.2, scale: 'spell', target: 'self',
    anim: 'cast', sfx: 'holy',
    effects: [{ k: 'nova', r: [5, 0.2], mult: [1.3, 0.18], el: 'magic', at: 'self', vis: 'holynova', heal: [10, 1.2] }],
    info: (r) => [dmgLine(r, [1.3, 0.18], 'magic damage'), `Heals allies ${rv([10, 1.2], r)}%`],
  }),
  def({
    id: 'sanctuary', cls: 'cleric', name: 'Sanctuary', tier: 10, icon: 'sanctuary',
    desc: 'Consecrate the ground: allies regenerate, enemies burn.',
    mana: [18, 1.4], cd: 12, cast: 0.25, recover: 0.2, scale: 'spell', target: 'point',
    anim: 'cast', sfx: 'holy',
    effects: [{ k: 'ground', r: [4, 0.15], dur: [6, 0.4], every: 0.5, mult: [0.3, 0.04], el: 'magic', at: 'target', heal: [3, 0.4], vis: 'sanctuary', status: { id: 'slow', dur: 0.8 } }],
    info: (r) => [`Heals ${rv([3, 0.4], r)}% per tick`, dmgLine(r, [0.3, 0.04], 'per tick'), `${rv([6, 0.4], r).toFixed(1)}s`],
  }),
  def({
    id: 'resurrect', cls: 'cleric', name: 'Resurrect', tier: 14, icon: 'resurrect',
    desc: 'Call a fallen ally back to the fight.',
    mana: [25, 0], cd: [30, -1.5], cast: 0.5, recover: 0.2, scale: 'none', target: 'ally',
    anim: 'cast', sfx: 'holy',
    effects: [{ k: 'revive', r: 14, pct: [40, 6], vis: 'holynova' }],
    info: (r) => [`Revives at ${rv([40, 6], r)}% HP`, `Radius 14`, `${rv([30, -1.5], r).toFixed(0)}s cooldown`],
  }),
  def({
    id: 'hammer', cls: 'cleric', name: 'Divine Hammer', tier: 18, icon: 'hammer',
    desc: 'Conjure a spinning hammer that spirals outward from you.',
    mana: [10, 0.8], cd: 0.35, cast: 0.14, recover: 0.2, scale: 'spell', target: 'point',
    anim: 'cast', sfx: 'holy',
    effects: [{ k: 'proj', proj: { vis: 'hammer', speed: 5, life: 2.5, r: 0.55, mult: [0.9, 0.12], el: 'magic', pierce: 99, spiral: true, ghost: true } }],
    info: (r) => [dmgLine(r, [0.9, 0.12], 'per hit'), 'Spirals outward, pierces'],
  }),
  def({
    id: 'ascension', cls: 'cleric', name: 'Ascension', tier: 24, icon: 'ascend',
    desc: 'For a few moments, the whole party is untouchable, and fully restored.',
    mana: [40, 2], cd: [80, -3], cast: 0.3, recover: 0.2, scale: 'none', target: 'self',
    anim: 'cast', sfx: 'levelup',
    effects: [
      { k: 'heal', pct: 100, r: 16, vis: 'holynova' },
      { k: 'buff', buff: 'ascension', r: 16, dur: [3, 0.25], mods: {}, invuln: true, bit: 2048, vis: 'buffGold' },
    ],
    info: (r) => [`Full party heal`, `${rv([3, 0.25], r).toFixed(1)}s invulnerable`, `${rv([80, -3], r)}s cooldown`],
  }),

  // ───────────────────────── IRONCLAD (tank) ─────────────────────────
  def({
    id: 'bash', cls: 'ironclad', name: 'Shield Bash', tier: 1, icon: 'bash',
    desc: 'Smash with your shield, briefly stunning the target.',
    mana: 0, cd: 0, cast: 0.14, recover: 0.3, scale: 'melee', target: 'point', reach: 1.9,
    anim: 'swing', sfx: 'bash',
    effects: [{ k: 'arc', range: 2, angle: 100, mult: [0.95, 0.13], el: 'phys', vis: 'slashWhite', status: { id: 'stun', dur: 0.35, chance: 0.4 }, knock: 0.6 }],
    info: (r) => [dmgLine(r, [0.95, 0.13]), '40% chance to stun'],
  }),
  def({
    id: 'taunt', cls: 'ironclad', name: 'Taunt', tier: 1, icon: 'taunt',
    desc: 'Force all nearby enemies to attack you, bracing against the blows.',
    mana: [4, 0.2], cd: 6, cast: 0.05, recover: 0.1, scale: 'none', target: 'self',
    anim: 'cast', sfx: 'shout',
    effects: [
      { k: 'taunt', r: [7, 0.4], dur: [4, 0.2], vis: 'shockwaveRed' },
      { k: 'buff', buff: 'taunt', r: 0, dur: 4, mods: { dmgReduce: [10, 1.5] }, bit: 512 },
    ],
    info: (r) => [`Radius ${rv([7, 0.4], r).toFixed(1)}`, `-${rv([10, 1.5], r)}% damage taken`],
  }),
  def({
    id: 'ironskin', cls: 'ironclad', name: 'Iron Skin', tier: 3, icon: 'ironskin',
    desc: 'Your flesh hardens to living iron.',
    mana: [8, 0.6], cd: 14, cast: 0.1, recover: 0.1, scale: 'none', target: 'self',
    anim: 'cast', sfx: 'buff',
    effects: [{ k: 'buff', buff: 'ironskin', r: 0, dur: [8, 0.6], mods: { armorPct: [60, 12], resAll: [10, 2] }, bit: 512, vis: 'buffGrey' }],
    info: (r) => [`+${rv([60, 12], r)}% armor`, `+${rv([10, 2], r)} all res`, `${rv([8, 0.6], r).toFixed(1)}s`],
  }),
  def({
    id: 'charge', cls: 'ironclad', name: 'Charge', tier: 6, icon: 'charge',
    desc: 'Barrel forward, flattening enemies in your path.',
    mana: [8, 0.6], cd: 5, cast: 0.05, recover: 0.1, scale: 'melee', target: 'point',
    anim: 'slam', sfx: 'charge',
    effects: [{ k: 'dash', speed: 22, maxDist: [9, 0.4], mult: [1.4, 0.2], el: 'phys', stun: 0.7, knock: 2.5, vis: 'shockwave' }],
    info: (r) => [dmgLine(r, [1.4, 0.2]), `Range ${rv([9, 0.4], r).toFixed(1)}`, 'Stuns & knocks back'],
  }),
  def({
    id: 'groundslam', cls: 'ironclad', name: 'Ground Slam', tier: 10, icon: 'slam',
    desc: 'Crash down, staggering everything around you.',
    mana: [12, 1], cd: 4.5, cast: 0.25, recover: 0.3, scale: 'melee', target: 'self',
    anim: 'slam', sfx: 'slam',
    effects: [{ k: 'nova', r: [4.5, 0.15], mult: [1.8, 0.24], el: 'phys', at: 'self', status: { id: 'stun', dur: [1, 0.06] }, knock: 1.5, vis: 'shockwave' }],
    info: (r) => [dmgLine(r, [1.8, 0.24]), `Stuns ${rv([1, 0.06], r).toFixed(1)}s`],
  }),
  def({
    id: 'guardian', cls: 'ironclad', name: 'Guardian Aura', tier: 14, icon: 'guardian',
    desc: 'Shelter allies behind your resolve, reducing all damage they take.',
    mana: [15, 1], cd: 22, cast: 0.1, recover: 0.1, scale: 'none', target: 'self',
    anim: 'cast', sfx: 'buff',
    effects: [{ k: 'buff', buff: 'guardian', r: 12, dur: [12, 1], mods: { dmgReduce: [15, 1.5], hpRegen: [3, 1] }, bit: 4096, vis: 'buffGold' }],
    info: (r) => [`-${rv([15, 1.5], r)}% party damage taken`, `+${rv([3, 1], r)} HP/sec`, `${rv([12, 1], r)}s`],
  }),
  def({
    id: 'spikes', cls: 'ironclad', name: 'Reflective Plating', tier: 18, icon: 'spikes',
    desc: 'Barbed plating flays any who dare to strike you.',
    mana: [12, 1], cd: 16, cast: 0.1, recover: 0.1, scale: 'none', target: 'self',
    anim: 'cast', sfx: 'buff',
    effects: [{ k: 'buff', buff: 'spikes', r: 0, dur: [10, 0.6], mods: { thorns: [40, 22], armorPct: [15, 3] }, bit: 512, vis: 'buffGrey' }],
    info: (r) => [`Reflects ${rv([40, 22], r)} damage per hit`, `+${rv([15, 3], r)}% armor`],
  }),
  def({
    id: 'laststand', cls: 'ironclad', name: 'Last Stand', tier: 24, icon: 'laststand',
    desc: 'You simply refuse to die. HP can not fall below 1 for the duration.',
    mana: [20, 1], cd: [70, -3], cast: 0.1, recover: 0.1, scale: 'none', target: 'self',
    anim: 'cast', sfx: 'shout',
    effects: [{ k: 'buff', buff: 'laststand', r: 0, dur: [5, 0.4], mods: { dmgPct: [30, 5] }, unkillable: true, bit: 2048, vis: 'buffGold' }],
    info: (r) => [`Unkillable for ${rv([5, 0.4], r).toFixed(1)}s`, `+${rv([30, 5], r)}% damage`],
  }),

  // ───────────────────────── HEXER (summons & curses) ─────────────────────────
  def({
    id: 'bonespear', cls: 'hexer', name: 'Bone Spear', tier: 1, icon: 'bone',
    desc: 'Hurl a sharpened shard of bone which punches through enemies.',
    mana: [1, 0.2], cd: 0, cast: 0.12, recover: 0.3, scale: 'spell', target: 'point',
    anim: 'cast', sfx: 'bone',
    effects: [{ k: 'proj', proj: { vis: 'bone', speed: 15, life: 0.9, r: 0.26, mult: [0.9, 0.13], el: 'magic', pierce: [1, 0.25] } }],
    info: (r) => [dmgLine(r, [0.9, 0.13], 'magic damage'), `Pierces ${Math.floor(rv([1, 0.25], r))}`],
  }),
  def({
    id: 'skeleton', cls: 'hexer', name: 'Raise Skeleton', tier: 1, icon: 'skeleton',
    desc: 'Pull a loyal skeleton warrior from the grave to fight for you.',
    mana: [8, 0.6], cd: 1.2, cast: 0.25, recover: 0.2, scale: 'spell', target: 'self',
    anim: 'cast', sfx: 'bone',
    effects: [{ k: 'summon', unit: 's_skeleton', count: 1, max: [2, 0.67], vis: 'bonePuff' }],
    info: (r) => [`Up to ${Math.floor(rv([2, 0.67], r))} skeletons`, 'Damage & HP scale with rank'],
  }),
  def({
    id: 'weaken', cls: 'hexer', name: 'Wither Curse', tier: 3, icon: 'curse',
    desc: 'Curse all foes in an area: they deal less damage and take more.',
    mana: [8, 0.6], cd: 8, cast: 0.2, recover: 0.2, scale: 'none', target: 'point',
    anim: 'cast', sfx: 'curse',
    effects: [{ k: 'debuff', r: [4, 0.2], at: 'target', status: { id: 'weaken', dur: [6, 0.4] }, dmgTaken: [15, 2], vis: 'curse' }],
    info: (r) => [`Foes deal 30% less`, `Foes take +${rv([15, 2], r)}%`, `${rv([6, 0.4], r).toFixed(1)}s`],
  }),
  def({
    id: 'poisonnova', cls: 'hexer', name: 'Plague Nova', tier: 6, icon: 'poison',
    desc: 'Vomit a ring of venomous bolts which poison all they touch.',
    mana: [12, 1], cd: 4, cast: 0.2, recover: 0.2, scale: 'spell', target: 'self',
    anim: 'cast', sfx: 'poison',
    effects: [{ k: 'proj', proj: { vis: 'poison', speed: 8, life: 0.8, r: 0.3, mult: [0.4, 0.06], el: 'poison', count: [16, 0], ring: true, status: { id: 'poison', dur: 4, dot: [0.5, 0.07] }, pierce: 2 } }],
    info: (r) => [dmgLine(r, [0.4, 0.06], 'impact'), `Poison ${pct(rv([0.5, 0.07], r) * 4)} over 4s`],
  }),
  def({
    id: 'soulsiphon', cls: 'hexer', name: 'Soul Siphon', tier: 10, icon: 'siphon',
    desc: 'Drain the life force of your foe into yourself.',
    mana: [8, 0.7], cd: 1, cast: 0.1, recover: 0.2, scale: 'spell', target: 'point',
    anim: 'cast', sfx: 'curse',
    effects: [{ k: 'beam', range: 9, mult: [1.6, 0.22], el: 'magic', steal: [0.25, 0.03], vis: 'siphon' }],
    info: (r) => [dmgLine(r, [1.6, 0.22], 'magic'), `Heals ${pct(rv([0.25, 0.03], r))} of damage`],
  }),
  def({
    id: 'corpseblast', cls: 'hexer', name: 'Corpse Blast', tier: 14, icon: 'corpse',
    desc: 'Detonate the corpses near your cursor in great gouts of bone and bile.',
    mana: [12, 1], cd: 2, cast: 0.15, recover: 0.2, scale: 'spell', target: 'point',
    anim: 'cast', sfx: 'explode',
    effects: [{ k: 'corpse', r: 5, blast: 3, mult: [2.4, 0.3], el: 'fire', maxCorpses: [3, 0.34], vis: 'explosionGreen' }],
    info: (r) => [dmgLine(r, [2.4, 0.3], 'per corpse'), `Up to ${Math.floor(rv([3, 0.34], r))} corpses`],
  }),
  def({
    id: 'golem', cls: 'hexer', name: 'Bone Golem', tier: 18, icon: 'golem',
    desc: 'Assemble an enormous golem of bone to crush your enemies.',
    mana: [28, 2], cd: 16, cast: 0.4, recover: 0.25, scale: 'spell', target: 'self',
    anim: 'cast', sfx: 'bone',
    effects: [{ k: 'summon', unit: 's_golem', count: 1, max: 1, vis: 'bonePuff' }],
    info: (r) => ['Summons a single golem', `Damage & HP scale with rank`],
  }),
  def({
    id: 'army', cls: 'hexer', name: 'Army of the Dead', tier: 24, icon: 'army',
    desc: 'The grave disgorges a ghoulish horde for a brief, terrible while.',
    mana: [40, 2], cd: 45, cast: 0.5, recover: 0.3, scale: 'spell', target: 'self',
    anim: 'cast', sfx: 'curse',
    effects: [{ k: 'summon', unit: 's_ghoul', count: [6, 0.67], max: 20, dur: [10, 0.6], vis: 'bonePuff' }],
    info: (r) => [`${Math.floor(rv([6, 0.67], r))} ghouls`, `${rv([10, 0.6], r).toFixed(0)}s`],
  }),
];

export const SKILLS: Record<string, SkillDef> = Object.fromEntries(list.map((s) => [s.id, s]));

export const CLASSES: Record<ClassId, ClassDef> = {
  reaver: {
    id: 'reaver', name: 'Reaver', title: 'Bladed Berserker', role: 'Melee DPS',
    desc: 'Wades into the thick of it with sword and fury. Hits everything in reach, and heals by spilling blood.',
    stats: { str: 25, dex: 15, vit: 20, ene: 8 },
    baseHp: 70, hpPerLevel: 9, hpPerVit: 3, baseMp: 22, mpPerLevel: 1.2, mpPerEne: 1.4,
    look: { skin: '#f8b878', hair: '#a81000', armor: '#888888', trim: '#d82800', body: 'plate', hat: 'none', weapon: 'sword' },
    skills: list.filter((s) => s.cls === 'reaver').map((s) => s.id),
  },
  spellweaver: {
    id: 'spellweaver', name: 'Spellweaver', title: 'Master of the Arcane', role: 'Ranged Magic',
    desc: 'Commands fire, ice, and storm from a safe distance. Fragile, but nothing outdoes a meteor for crowd control.',
    stats: { str: 8, dex: 14, vit: 14, ene: 32 },
    baseHp: 48, hpPerLevel: 6, hpPerVit: 2.5, baseMp: 40, mpPerLevel: 2.2, mpPerEne: 2.0,
    look: { skin: '#f8b878', hair: '#fcfcfc', armor: '#2038ec', trim: '#fcd8a8', body: 'robe', hat: 'wizard', weapon: 'staff' },
    skills: list.filter((s) => s.cls === 'spellweaver').map((s) => s.id),
  },
  gunner: {
    id: 'gunner', name: 'Gunner', title: 'Demolitions Expert', role: 'Ranged Physical',
    desc: 'Brings guns to a sword fight, and explosives to a gun fight. Mines, turrets, rockets, and the occasional orbital strike.',
    stats: { str: 12, dex: 30, vit: 16, ene: 10 },
    baseHp: 58, hpPerLevel: 7, hpPerVit: 2.8, baseMp: 28, mpPerLevel: 1.5, mpPerEne: 1.6,
    look: { skin: '#c84c0c', hair: '#503000', armor: '#887000', trim: '#f87858', body: 'coat', hat: 'visor', weapon: 'gun' },
    skills: list.filter((s) => s.cls === 'gunner').map((s) => s.id),
  },
  cleric: {
    id: 'cleric', name: 'Cleric', title: 'Light of the Warp', role: 'Healer / Support',
    desc: 'Keeps the party alive with heals, blessings and resurrection, while burning unholy things with radiant fire.',
    stats: { str: 14, dex: 10, vit: 18, ene: 26 },
    baseHp: 60, hpPerLevel: 7.5, hpPerVit: 2.8, baseMp: 36, mpPerLevel: 2, mpPerEne: 1.9,
    look: { skin: '#fcd8a8', hair: '#f8b800', armor: '#fcfcfc', trim: '#f8b800', body: 'robe', hat: 'halo', weapon: 'mace' },
    skills: list.filter((s) => s.cls === 'cleric').map((s) => s.id),
  },
  ironclad: {
    id: 'ironclad', name: 'Ironclad', title: 'Unbreakable Bulwark', role: 'Tank',
    desc: 'A walking fortress. Draws every eye in the room and shrugs off blows which would flatten anyone else.',
    stats: { str: 22, dex: 8, vit: 30, ene: 8 },
    baseHp: 90, hpPerLevel: 11, hpPerVit: 3.6, baseMp: 20, mpPerLevel: 1, mpPerEne: 1.3,
    look: { skin: '#fcd8a8', hair: '#503000', armor: '#3cbcfc', trim: '#fcfcfc', body: 'heavy', hat: 'helm', weapon: 'hammer' },
    skills: list.filter((s) => s.cls === 'ironclad').map((s) => s.id),
  },
  hexer: {
    id: 'hexer', name: 'Hexer', title: 'Lord of the Grave', role: 'Summoner / Curses',
    desc: 'Why fight when the dead can do it for you? Commands skeletal legions, curses foes, and detonates the fallen.',
    stats: { str: 10, dex: 12, vit: 16, ene: 30 },
    baseHp: 54, hpPerLevel: 6.5, hpPerVit: 2.6, baseMp: 38, mpPerLevel: 2.1, mpPerEne: 1.9,
    look: { skin: '#bcbcbc', hair: '#000000', armor: '#6844fc', trim: '#58f898', body: 'cloak', hat: 'hood', weapon: 'scythe' },
    skills: list.filter((s) => s.cls === 'hexer').map((s) => s.id),
  },
};

export const CLASS_IDS = Object.keys(CLASSES) as ClassId[];

/** The two skills every new hero starts with, bound to LMB / RMB. */
export function starterSkills(cls: ClassId): string[] {
  return CLASSES[cls].skills.slice(0, 2);
}

export type { EffectDef };
